import { REVISION_ALIAS } from 'src/constants/config.constants';
import {
  CurriculumAssignmentContent,
  CustomContentId,
} from 'src/interfaces/assignment.types';
import { getAmpConfig } from 'src/services/amp-config.service';

export const getImagePath = (path?: string) => {
  return path?.includes('http') ? path : `${getAmpConfig().apps}/${path}`;
};

export const curriculumURLs = {
  getUnitURL: (unitKey?: string) => {
    return `${
      getAmpConfig().apps
    }/curriculum/#/unit/${unitKey}:${REVISION_ALIAS}`;
  },
  getLessonBriefURL: (
    unitKey?: string,
    lessonKey?: string,
    chapterKey?: string,
  ) => {
    const unitURL = curriculumURLs.getUnitURL(unitKey);
    return `${unitURL}/lesson/${lessonKey}/chapter/${chapterKey}`;
  },
  getCardstackURL: (
    unitKey?: string,
    chapterKey?: string,
    lessonKey?: string,
    cardstackKey?: string,
  ) => {
    const unitURL = curriculumURLs.getUnitURL(unitKey);
    return `${unitURL}/cardstack/${cardstackKey}/${chapterKey}/${lessonKey}`;
  },
  getCustomContentUrl: (customContentId: string) => {
    const rootUrl = `${getAmpConfig().apps}/curriculum`;
    return `${rootUrl}/#/custom/lesson/${customContentId}/cardstack/`;
  },
};

export const classroomURLs = {
  getRootURL: () => {
    return `${getAmpConfig().apps}/classroom/#/student`;
  },
  getCustomContentURL: (customContentId: CustomContentId) => {
    const rootURL = classroomURLs.getRootURL();
    return `${rootURL}/custom/lesson/${customContentId}`;
  },
  getRevisionAlias: (unitKey: string) => {
    const hasRevisionAlias = unitKey.match(/(\d{4}-\d{4}|latest)/);

    return hasRevisionAlias ? '' : `:${REVISION_ALIAS}`;
  },
  getCurriculumLessonClassroomURL: (
    unitKey: string,
    chapterKey: string,
    lessonKey: string,
    unitRevisionAlias: string,
  ): string => {
    const rootURL = classroomURLs.getRootURL();
    const revisionAlias = unitRevisionAlias
      ? `:${unitRevisionAlias}`
      : classroomURLs.getRevisionAlias(unitKey);

    return `${rootURL}/unit/${unitKey}${revisionAlias}/lesson/${lessonKey}/chapter/${chapterKey}`;
  },
  getURLFromContent: (content: CurriculumAssignmentContent) => {
    if (content.customContentId) {
      return classroomURLs.getCustomContentURL(content.customContentId);
    }

    return classroomURLs.getCurriculumLessonClassroomURL(
      content.unitKey ?? '',
      content.chapterKey ?? '',
      content.lessonKey ?? '',
      content.unitRevisionAlias ?? '',
    );
  },
};

export const isFullBynderLink = (link: string) => {
  const bynderAssetsRootPath = `${getAmpConfig().assets}/m/`;

  return link.startsWith(bynderAssetsRootPath);
};

export const isDesmosAssetsLink = (link: string) => {
  const desmosAssetsRootPath = 'https://uploads.desmos.com/';

  return link.startsWith(desmosAssetsRootPath);
};

export const getFullBynderLink = (resourcePath: string) => {
  return `${getAmpConfig().assets}/${resourcePath}`;
};

export const isHttpsMissing = (url: string) => {
  const rootURLPattern = /^(\w|\d|-)+\./gim;
  // if it doesn't have http than https too
  return rootURLPattern.test(url);
};

export const addHttps = (url: string) => `https://${url}`;

// this method ignores relative URLs. e.g. /#/test-url WONT BE converted to https://#/test-url
export const fixMissingHttps = (url: string | undefined = '') =>
  isHttpsMissing(url) ? addHttps(url) : url;

const ALLOWED_PROTOCOLS = ['http:', 'https:'];
// eslint-disable-next-line no-script-url
const DANGEROUS_PROTOCOLS = ['javascript:', 'data:', 'vbscript:', 'about:'];

export const isValidUrl = (url: string): boolean => {
  try {
    const parsedUrl = new URL(url);
    return ALLOWED_PROTOCOLS.includes(parsedUrl.protocol.toLowerCase());
  } catch {
    return false;
  }
};

export const containsDangerousProtocol = (url: string): boolean => {
  const lowercaseUrl = url.toLowerCase().trim();
  return DANGEROUS_PROTOCOLS.some((protocol) =>
    lowercaseUrl.startsWith(protocol),
  );
};

export const sanitizeUrl = (url: string | undefined): string | undefined => {
  if (!url || typeof url !== 'string') {
    return undefined;
  }

  const trimmedUrl = url.trim();

  if (!trimmedUrl) {
    return undefined;
  }

  if (containsDangerousProtocol(trimmedUrl)) {
    return undefined;
  }

  if (!isValidUrl(trimmedUrl)) {
    return undefined;
  }

  try {
    const parsedUrl = new URL(trimmedUrl);
    const hostname = parsedUrl.hostname.toLowerCase();

    // Allow desmos.com and all its subdomains
    if (hostname === 'desmos.com' || hostname.endsWith('.desmos.com')) {
      return trimmedUrl;
    }

    // Allow amplify.com and all its subdomains
    if (hostname === 'amplify.com' || hostname.endsWith('.amplify.com')) {
      return trimmedUrl;
    }

    return undefined;
  } catch {
    return undefined;
  }
};

export const sanitizeInsightUrl = (url: string | undefined): string => {
  const sanitized = sanitizeUrl(url);
  return sanitized ?? '#';
};

export const sanitizeInsightThumbnailUrl = (
  thumbnailUrl: string | undefined,
): string | undefined => {
  if (!thumbnailUrl) return undefined;

  // Handle relative URLs by converting to full Bynder URLs first
  let fullUrl: string;

  if (isDesmosAssetsLink(thumbnailUrl) || isFullBynderLink(thumbnailUrl)) {
    fullUrl = thumbnailUrl;
  } else {
    // Convert relative paths to full Bynder URLs
    fullUrl = getFullBynderLink(thumbnailUrl);
  }

  // Then sanitize the full URL
  return sanitizeUrl(fullUrl);
};
